import React, { useState, useRef, useEffect } from 'react';
import IosCard from '../../components/iOSCard';
import { 
    UserIcon, KeyIcon, CheckCircleIcon, CameraIcon, 
    ShieldCheckIcon, PencilIcon, StarIcon, AcademicCapIcon, 
    FireIcon, TrophyIcon, LockClosedIcon, EnvelopeIcon,
    ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { auth, db } from '../../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';

const StudentProfile = () => {
    const fileInputRef = useRef(null);
    const [isEditing, setIsEditing] = useState(false);
    const [activeTab, setActiveTab] = useState('personal'); 
    const [statusModal, setStatusModal] = useState({ show: false, message: '', type: 'success' });
    const [loading, setLoading] = useState(true);

    // التحقق من طريقة تسجيل الدخول (هل هي Gmail أم إيميل عادي)
    const isGoogleUser = auth.currentUser?.providerData.some(p => p.providerId === 'google.com');

    const [userData, setUserData] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        specialty: "طالب سادس علمي",
        bio: "",
        image: null 
    });

    const [passwordData, setPasswordData] = useState({
        current: "",
        new: "",
        confirm: ""
    });

    useEffect(() => {
        const fetchUserData = async () => {
            if (!auth.currentUser) return;
            try {
                const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setUserData(data);
                    setFormData({
                        name: data.name || "طالب مبدع",
                        specialty: data.specialty || "طالب سادس علمي",
                        bio: data.bio || "لا توجد نبذة حالياً.",
                        image: data.profileImage || null
                    });
                }
            } catch (error) {
                console.error("Error fetching profile:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, []);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setFormData({ ...formData, image: reader.result });
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        try {
            const userRef = doc(db, "users", auth.currentUser.uid);
            await updateDoc(userRef, {
                name: formData.name,
                bio: formData.bio,
                profileImage: formData.image
            });
            setIsEditing(false);
            showModal('تم تحديث ملفك بنجاح! ✨');
        } catch (error) {
            showModal('عذراً، فشل تحديث البيانات', 'error');
        }
    };

    const handleChangePassword = async () => {
        if (passwordData.new !== passwordData.confirm) return showModal('كلمتا السر غير متطابقتين', 'error');
        if (passwordData.new.length < 6) return showModal('كلمة السر يجب أن تكون 6 رموز فأكثر', 'error');

        try {
            const user = auth.currentUser;
            const credential = EmailAuthProvider.credential(user.email, passwordData.current);
            // خطوة الأمان: إعادة المصادقة للتأكد من هوية المستخدم
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, passwordData.new);
            
            setPasswordData({ current: "", new: "", confirm: "" });
            showModal('تم تغيير كلمة السر بنجاح 🛡️');
        } catch (error) {
            showModal('عذراً، كلمة السر الحالية خاطئة', 'error');
        }
    };

    const showModal = (msg, type = 'success') => {
        setStatusModal({ show: true, message: msg, type });
        setTimeout(() => setStatusModal({ show: false, message: '', type: 'success' }), 3000);
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#007AFF] border-t-transparent rounded-full animate-spin"></div></div>;

    return (
        <div style={{ fontFamily: "'Cairo', sans-serif" }} className="max-w-5xl mx-auto space-y-8 animate-in fade-in zoom-in-95 duration-700 pb-20 text-right" dir="rtl">
            
            {/* الهيدر الملكي */}
            <div className="relative h-64 mb-20">
                <div className="absolute inset-0 h-48 bg-gradient-to-r from-purple-600 to-[#007AFF] rounded-[48px] shadow-2xl overflow-hidden border-white/20 border z-0">
                    <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                </div>

                <div className="absolute -bottom-10 right-10 flex items-end gap-6 z-10">
                    <div className="relative">
                        <div className="w-36 h-36 bg-white rounded-[40px] border-4 border-[#F2F2F7] overflow-hidden shadow-2xl flex items-center justify-center">
                            {formData.image ? <img src={formData.image} className="w-full h-full object-cover" alt="profile" /> : <div className="w-full h-full bg-purple-50 flex items-center justify-center text-purple-600 text-5xl font-black">{formData.name.charAt(0)}</div>}
                        </div>
                        {isEditing && (
                            <button onClick={() => fileInputRef.current.click()} className="absolute bottom-[-10px] left-[-10px] p-3 bg-purple-600 text-white rounded-2xl shadow-xl border-4 border-white z-20 hover:scale-110 transition-transform"><CameraIcon className="w-6 h-6" /></button>
                        )}
                        <input type="file" ref={fileInputRef} onChange={handleImageChange} className="hidden" accept="image/*" />
                    </div>
                    <div className="mb-14 text-white">
                        <h2 className="text-3xl font-black drop-shadow-lg">{formData.name}</h2>
                        <p className="text-sm font-bold opacity-90">{formData.specialty}</p>
                    </div>
                </div>
            </div>

            {/* 📊 بطاقات البيانات الحقيقية */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="ios-glass p-6 rounded-[35px] flex items-center gap-4 border-white/40 shadow-sm">
                    <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center text-orange-600"><FireIcon className="w-8 h-8" /></div>
                    <div><p className="text-[10px] font-black text-gray-400 uppercase">النقاط (XP)</p><h4 className="text-2xl font-black text-gray-800">{userData?.totalStudyMinutes || 0}</h4></div>
                </div>
                <div className="ios-glass p-6 rounded-[35px] flex items-center gap-4 border-white/40 shadow-sm">
                    <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600"><StarIcon className="w-8 h-8" /></div>
                    <div><p className="text-[10px] font-black text-gray-400 uppercase">التقدم العام</p><h4 className="text-2xl font-black text-gray-800">{userData?.overallProgress || 0}%</h4></div>
                </div>
                <div className="ios-glass p-6 rounded-[35px] flex items-center gap-4 border-white/40 shadow-sm">
                    <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600"><TrophyIcon className="w-8 h-8" /></div>
                    <div><p className="text-[10px] font-black text-gray-400 uppercase">التصنيف</p><h4 className="text-2xl font-black text-gray-800">{userData?.platformRank || "#--"}</h4></div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="space-y-6">
                    <div className="ios-glass p-4 rounded-[30px] border-white/40 shadow-sm space-y-2">
                        <button onClick={() => setActiveTab('personal')} className={`w-full flex items-center gap-3 p-3.5 rounded-2xl font-black text-sm transition-all ${activeTab === 'personal' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-600 hover:bg-white/50'}`}><UserIcon className="w-5 h-5" /> الحساب</button>
                        <button onClick={() => setActiveTab('security')} className={`w-full flex items-center gap-3 p-3.5 rounded-2xl font-black text-sm transition-all ${activeTab === 'security' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-600 hover:bg-white/50'}`}><ShieldCheckIcon className="w-5 h-5" /> الأمان</button>
                    </div>
                </div>

                <div className="md:col-span-2">
                    <AnimatePresence mode="wait">
                        {activeTab === 'personal' ? (
                            <motion.div key="personal" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                <IosCard title="بيانات الطالب" icon={<PencilIcon />} color="purple">
                                    <div className="space-y-6 p-2">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase mr-2">الاسم بالكامل</label>
                                            <input disabled={!isEditing} type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className={`w-full p-4 rounded-2xl font-bold text-gray-700 ${isEditing ? 'bg-white/60 border border-purple-200' : 'bg-transparent border-transparent'}`} />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase mr-2">النبذة التعريفية</label>
                                            <textarea disabled={!isEditing} rows="3" value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} className={`w-full p-4 rounded-2xl font-bold text-gray-700 ${isEditing ? 'bg-white/60 border border-purple-200' : 'bg-transparent border-transparent'}`} />
                                        </div>
                                        <button onClick={() => isEditing ? handleSave() : setIsEditing(true)} className={`w-full py-5 rounded-[24px] font-black text-sm transition-all ${isEditing ? 'bg-purple-600 text-white shadow-lg' : 'bg-white border border-gray-100 text-gray-600 shadow-sm'}`}>
                                            {isEditing ? 'حفظ التعديلات' : 'تعديل الملف الشخصي'}
                                        </button>
                                    </div>
                                </IosCard>
                            </motion.div>
                        ) : (
                            <motion.div key="security" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                <IosCard title="أمان الحساب" icon={<LockClosedIcon />} color="blue">
                                    <div className="space-y-6 p-2">
                                        <div className="p-4 bg-blue-50/50 rounded-2xl border border-blue-100 flex items-start gap-3">
                                            <EnvelopeIcon className="w-6 h-6 text-blue-500 mt-1" />
                                            <div>
                                                <h4 className="font-black text-sm text-blue-900">البريد الإلكتروني المرتبط</h4>
                                                <p className="text-xs text-blue-600 font-bold">{auth.currentUser?.email}</p>
                                            </div>
                                        </div>

                                        {/* إذا كان المستخدم مسجلاً عبر Google، لا يمكنه تغيير الباسورد من هنا */}
                                        {isGoogleUser ? (
                                            <div className="p-5 bg-orange-50 rounded-3xl border border-orange-100 flex items-center gap-4">
                                                <ExclamationCircleIcon className="w-8 h-8 text-orange-500" />
                                                <p className="text-xs font-bold text-orange-700 leading-relaxed">
                                                    أنت مسجل دخول عبر حساب Google. لإدارة أمان حسابك أو تغيير كلمة السر، يرجى مراجعة إعدادات حساب Google الخاص بك.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase mr-2">كلمة السر الحالية</label>
                                                    <input type="password" value={passwordData.current} onChange={(e) => setPasswordData({...passwordData, current: e.target.value})} className="w-full p-4 rounded-2xl bg-white/60 border border-gray-200 outline-none font-bold" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase mr-2">كلمة السر الجديدة</label>
                                                    <input type="password" value={passwordData.new} onChange={(e) => setPasswordData({...passwordData, new: e.target.value})} className="w-full p-4 rounded-2xl bg-white/60 border border-gray-200 outline-none font-bold" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase mr-2">تأكيد كلمة السر</label>
                                                    <input type="password" value={passwordData.confirm} onChange={(e) => setPasswordData({...passwordData, confirm: e.target.value})} className="w-full p-4 rounded-2xl bg-white/60 border border-gray-200 outline-none font-bold" />
                                                </div>
                                                <button onClick={handleChangePassword} className="w-full py-5 bg-[#007AFF] text-white rounded-[24px] font-black text-sm shadow-xl active:scale-95 transition-transform">تحديث كلمة السر 🛡️</button>
                                            </div>
                                        )}
                                    </div>
                                </IosCard>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* رسالة النجاح أو الخطأ */}
            <AnimatePresence>
                {statusModal.show && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6 pointer-events-none">
                        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }} className={`${statusModal.type === 'success' ? 'bg-white/80' : 'bg-red-50/90'} backdrop-blur-2xl p-10 rounded-[50px] shadow-2xl border border-white/50 flex flex-col items-center gap-4 text-center`}>
                            <div className={`w-20 h-20 ${statusModal.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'} rounded-full flex items-center justify-center shadow-lg animate-bounce`}>
                                {statusModal.type === 'success' ? <CheckCircleIcon className="w-12 h-12 text-white" /> : <ShieldCheckIcon className="w-12 h-12 text-white" />}
                            </div>
                            <h3 className={`text-xl font-black ${statusModal.type === 'success' ? 'text-gray-800' : 'text-red-900'}`}>{statusModal.message}</h3>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default StudentProfile;